/**
 * Who sees what on the multi-project runs view (2026-09-08).
 *
 * Role source: the same permission map the Usage & Cost dashboard reads
 * (useUserPermissions → FileContext GROUP_PERMISSIONS). `view_all_projects` is
 * granted to the Admin group only; Super Users and Users do not hold it.
 *
 *   admin       → every queued / active / done entry + the fleet board
 *   everyone else → only entries whose project_id is one they can open, no
 *                   fleet board (positions and ETAs still show)
 *
 * The scoping itself is CLIENT-ENFORCED: the Pipeline queue API carries no
 * user identity, so the browser passes `project_ids` and the server filters
 * on it. It is a view filter, not authorization.
 */

export const canSeeWholeQueue = (permissions) => {
  if (!permissions) return false;
  const perms = Array.isArray(permissions.permissions) ? permissions.permissions : [];
  return perms.includes("view_all_projects") || Boolean(permissions.isAdmin);
};

/**
 * RSA projects are the ones that can have runs: ProjectType rsa-existing or
 * rsa-model (the historical misspellings are accepted, as ProjectCard does).
 */
const RSA_PROJECT_TYPES = new Set(["rsa-existing", "rsa-exiting", "rsa-model", "rsaa-model"]);
export const isRsaProject = (project) =>
  RSA_PROJECT_TYPES.has(String(project?.ProjectType ?? project?.project_type ?? "").trim().toLowerCase());

export const rsaProjectCount = (projects) =>
  (Array.isArray(projects) ? projects : []).filter(isRsaProject).length;

/**
 * Who gets the "Multi-project runs" view at all (2026-09-17): admins, and
 * anyone with MORE THAN ONE RSA project (existing or model). With a single
 * RSA project the project's own dashboard already shows everything.
 */
export const canSeeMultiProjectRuns = (permissions, projects) =>
  canSeeWholeQueue(permissions) || rsaProjectCount(projects) > 1;

/**
 * The Project Overview is also offered to the team whose e-mail domain opens the
 * demo projects (config/crash_sources.json "demo".emailDomains). A UI hint only:
 * the server decides which projects each user may open.
 */
const DEMO_TEAM_DOMAINS = ["rdvsystems.com"];
export const isDemoTeam = (email) =>
  DEMO_TEAM_DOMAINS.includes(String(email || "").split("@").pop().trim().toLowerCase()) &&
  String(email || "").includes("@");

export const canSeeProjectOverview = (permissions, projects, email) =>
  canSeeMultiProjectRuns(permissions, projects) || isDemoTeam(email);

/** Project ids (strings) from any of the project-list shapes the app uses. */
export const visibleProjectIds = (projects) =>
  Array.from(
    new Set(
      (Array.isArray(projects) ? projects : [])
        .map((p) => String(p?.id ?? p?.Id ?? p?.project_id ?? "").trim())
        .filter(Boolean),
    ),
  );
